// The end of a settled forecast's story on the timeline: a gold rule at the
// moment it resolved, a short connector from the probability trace's last value to where the
// outcome landed, and the canvas past that moment dimmed. Drawn in place of the violet
// resolution-date line, which stays for every forecast that has not resolved.
// Logic lives in lib/timeline/resolvedOutcome.js.

import { themeColors } from "../../lib/colors.js";
import { extractMultiway } from "../../lib/forecast/multiway.js";
import { resolvedTraceEnd, traceY } from "../../lib/timeline/resolvedOutcome.js";

const LABEL_FONT = 12;

/**
 * The canvas half: dim overlay, gold rule, connector and outcome dot. Rendered inside the
 * timeline content (the same box the today line and the probability trace are drawn in).
 *
 * `trace` carries what the probability plot is drawn from, so the connector starts on the line
 * actually on screen; with no plot (no forecast history) only the rule and the dim are drawn.
 */
export function ResolvedOutcomeMark({ x, vw, theme, spineY, streamHalf, capMs, trace }) {
  if (x == null || !Number.isFinite(x) || x > vw + 40) return null;
  const P = themeColors(theme);
  const left = Math.max(-1, x);
  const onScreen = x > -10;
  const plotShown =
    !!trace &&
    trace.forecastTracking &&
    (trace.active?.history?.length > 0 || trace.pointerCount > 0) &&
    !!trace.active?.contract?.resolution_date &&
    (trace.active?.contract?.probability != null ||
      (trace.perOutcome && !!extractMultiway(trace.active?.contract)));
  const end =
    onScreen && plotShown
      ? resolvedTraceEnd({
          active: trace.active,
          displayContract: trace.displayContract,
          asyncMoments: trace.asyncMoments,
          hoveredBinLabel: trace.hoveredBinLabel,
          status: trace.status,
          verdict: trace.verdict,
          capMs,
          allMultiway: trace.allMultiway,
          perOutcome: trace.perOutcome,
        })
      : null;
  const y0 = end ? traceY(end.pLast, spineY, streamHalf) : null;
  const y1 = end ? traceY(end.pOutcome, spineY, streamHalf) : null;
  return (
    <>
      {/* After the question settled: the canvas recedes, the bars stay on top. */}
      <div
        data-testid="resolved-after"
        style={{
          position: "absolute",
          left,
          top: 0,
          width: Math.max(0, vw + 40 - left),
          height: "100%",
          background: `color-mix(in srgb, ${P.bg} 55%, transparent)`,
          zIndex: 4,
          pointerEvents: "none",
        }}
      />
      {onScreen && (
        <div
          data-testid="resolved-line"
          style={{
            position: "absolute",
            left: x,
            top: 0,
            width: 1,
            height: "100%",
            background: P.gold,
            opacity: 0.55,
            transform: "translateX(-50%)",
            zIndex: 5,
            pointerEvents: "none",
          }}
        />
      )}
      {end && (
        <svg
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            overflow: "visible",
            zIndex: 5,
            pointerEvents: "none",
          }}
        >
          {/* From the forecast's last value to where the outcome landed. */}
          <line
            x1={x}
            x2={x}
            y1={y0}
            y2={y1}
            stroke={P.gold}
            strokeWidth={2}
            strokeLinecap="round"
          />
          <circle cx={x} cy={y0} r={2.5} fill={P.gold} />
          <circle cx={x} cy={y1} r={6} fill={P.gold} />
          <path
            d={`M ${x - 2.6} ${y1 + 0.1} L ${x - 0.7} ${y1 + 2} L ${x + 2.7} ${y1 - 1.9}`}
            stroke={P.bg}
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      )}
    </>
  );
}

/** The gold rule's continuation through the date axis, in place of the violet date tick. */
export function ResolvedAxisTick({ x, vw, theme }) {
  if (x == null || !Number.isFinite(x) || x < -10 || x > vw + 10) return null;
  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: 0,
        width: 1,
        height: "100%",
        background: themeColors(theme).gold,
        opacity: 0.55,
        transform: "translateX(-50%)",
        pointerEvents: "none",
      }}
    />
  );
}

/**
 * The label half, "Resolved: <outcome>", on the timeline's label row, right of the gold rule.
 * A settled forecast shows no Today label (useTimelineGeometry `showTodayLine`), so nothing
 * shares the row with it.
 */
export function ResolvedOutcomeLabel({ x, vw, text, top, color }) {
  if (x == null || !Number.isFinite(x) || x < -10 || x > vw + 10) return null;
  return (
    <div
      data-testid="resolved-label"
      style={{
        position: "absolute",
        left: x + 6,
        top,
        maxWidth: 280,
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontSize: LABEL_FONT,
        lineHeight: "14px",
        color,
        letterSpacing: 0,
        whiteSpace: "nowrap",
        fontWeight: 600,
        zIndex: 15,
        pointerEvents: "none",
      }}
    >
      {text}
    </div>
  );
}
